import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Phone, MessageCircle, Flame, ChevronRight, CalendarClock, Clock, Sparkles, Plus, AlertTriangle, Loader2, MessageSquareText } from "lucide-react";
import MobileShell from "@/components/mobile/MobileShell";
import BrainPanel from "@/components/BrainPanel";
import QuickMsgSheet from "@/components/QuickMsgSheet";
import SwipeActions from "@/components/mobile/SwipeActions";
import { api } from "@/lib/api";
import { telLink, waLink, buzz } from "@/lib/mobile";
import { fmtDateTimeIT, todayISO } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";

export default function MobileToday() {
  const { user } = useAuth();
  const [loading, setLoading]       = useState(true);
  const [kpi, setKpi]               = useState({ daily: 0, hot: 0, expired: 0, appts: 0 });
  const [dailyClients, setDailyClients] = useState([]);
  const [appts, setAppts]           = useState([]);
  const [msgClient, setMsgClient]   = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const today = todayISO();
        const [over, daily, expired, reminders] = await Promise.all([
          api.get("/dashboard/overview"),
          api.get("/clients", { params: { view: "daily", limit: 30, sort: "stato_hot" } }),
          api.get("/clients", { params: { view: "expired", limit: 1 } }),
          api.get("/reminders", { params: { fromDate: `${today}T00:00:00`, toDate: `${today}T23:59:59`, limit: 50 } }),
        ]);
        setKpi({
          daily:   daily.data.total || 0,
          hot:     over.data.kpi?.hotClients || 0,
          expired: expired.data.total || 0,
          appts:   (reminders.data.items || []).filter((r) => !r.completed && r.appointment).length,
        });
        setDailyClients((daily.data.items || []).slice(0, 15));
        setAppts((reminders.data.items || []).filter((r) => !r.completed).sort((a, b) => a.when.localeCompare(b.when)).slice(0, 5));
      } catch (e) {
        console.error("MobileToday:", e);
      } finally { setLoading(false); }
    })();
  }, []);

  const firstName = user?.name?.split(" ")[0] || "";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Buongiorno" : hour < 18 ? "Buon pomeriggio" : "Buonasera";

  return (
    <MobileShell title={greeting + (firstName ? `, ${firstName}` : "")} subtitle="Best Agent · Oggi">

      {/* ── Quick actions ── */}
      <div className="grid grid-cols-2 gap-2.5 mb-5">
        <button
          onClick={() => { buzz(); nav("/m/aggiungi"); }}
          data-testid="btn-quick-add"
          className="flex items-center gap-2.5 px-4 py-3.5 active:scale-[0.97] transition-transform"
          style={{ background: "#0A192F", borderRadius: 10, border: "1px solid rgba(255,255,255,0.08)" }}
        >
          <div className="w-7 h-7 flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.1)", borderRadius: 6 }}>
            <Plus className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <span className="text-[13px] font-semibold text-white">Nuovo cliente</span>
        </button>
        <button
          onClick={() => { buzz(); nav("/m/comandante"); }}
          data-testid="btn-quick-ai"
          className="flex items-center gap-2.5 px-4 py-3.5 active:scale-[0.97] transition-transform"
          style={{ background: "linear-gradient(135deg, #B7955B 0%, #A18048 100%)", borderRadius: 10 }}
        >
          <div className="w-7 h-7 flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.2)", borderRadius: 6 }}>
            <Sparkles className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <span className="text-[13px] font-semibold text-white">Comandante AI</span>
        </button>
      </div>

      {/* ── KPI tiles ── */}
      <div className="grid grid-cols-2 gap-2.5 mb-5">
        <KpiTile label="Da chiamare" value={kpi.daily}   color="#0A192F" icon={Phone}         to="/m/clienti?view=daily" />
        <KpiTile label="Lead caldi"  value={kpi.hot}     color="#B7955B" icon={Flame}          to="/m/clienti?view=hot" />
        <KpiTile label="Scaduti"     value={kpi.expired} color="#DC2626" icon={AlertTriangle}  to="/m/clienti?view=expired" warn={kpi.expired > 0} />
        <KpiTile label="Appuntamenti" value={kpi.appts}  color="#0891B2" icon={CalendarClock}  to="/m/agenda" />
      </div>

      {/* ── Appointments today ── */}
      {appts.length > 0 && (
        <Section title="Agenda di oggi" link="/m/agenda">
          <div className="space-y-2">
            {appts.map((r) => <ApptRow key={r.id} r={r} />)}
          </div>
        </Section>
      )}

      {/* ── Comandante AI Brain ── */}
      <div className="mb-5">
        <BrainPanel />
      </div>

      {/* ── Daily call list ── */}
      <Section title={`Da chiamare oggi`} count={kpi.daily} link="/m/clienti?view=daily">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#B7955B" }} />
          </div>
        ) : dailyClients.length === 0 ? (
          <div className="flex flex-col items-center py-8 text-center bg-white" style={{ borderRadius: 12, border: "1px solid #E8ECF2" }}>
            <Phone className="w-8 h-8 mb-3" style={{ color: "#E8ECF2" }} strokeWidth={1} />
            <p className="text-[13px] font-medium text-slate-500">Nessuna chiamata oggi</p>
            <p className="text-[12px] text-slate-400 mt-1">Ottimo lavoro!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {dailyClients.map((c) => <ClientCallRow key={c.id} c={c} onMsg={setMsgClient} />)}
          </div>
        )}
      </Section>

      {/* Sheet messaggi preimpostati */}
      <QuickMsgSheet client={msgClient} open={!!msgClient} onOpenChange={(v) => { if (!v) setMsgClient(null); }} />
    </MobileShell>
  );
}

/* ─── KPI Tile ─── */
function KpiTile({ label, value, color, icon: Icon, to, warn }) {
  return (
    <Link
      to={to}
      data-testid={`kpi-${label}`}
      className="flex flex-col p-4 active:scale-[0.97] transition-transform"
      style={{
        background: "#fff",
        border: `1px solid ${warn && value > 0 ? "#FECACA" : "#E8ECF2"}`,
        borderRadius: 12,
        boxShadow: "0 1px 3px rgba(10,25,47,0.05)",
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="w-8 h-8 flex items-center justify-center" style={{ background: `${color}15`, borderRadius: 8 }}>
          <Icon className="w-4 h-4" strokeWidth={1.8} style={{ color: warn && value > 0 ? "#DC2626" : color }} />
        </div>
        <ChevronRight className="w-3.5 h-3.5 text-slate-300" strokeWidth={2} />
      </div>
      <p className="font-serif tabular-nums leading-none" style={{ fontSize: "1.8rem", fontWeight: 300, color: warn && value > 0 ? "#DC2626" : "#0D1829" }}>
        {value}
      </p>
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] mt-1.5" style={{ color: "#94A3B8" }}>
        {label}
      </p>
    </Link>
  );
}

/* ─── Section wrapper ─── */
function Section({ title, link, count, children }) {
  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-2.5 px-0.5">
        <div className="flex items-center gap-2">
          <h2 className="font-serif text-[15px] font-light text-slate-900">{title}</h2>
          {count > 0 && (
            <span className="text-[10px] font-bold tabular-nums px-1.5 py-0.5" style={{ background: "#F0F2F5", color: "#64748B", borderRadius: 4 }}>
              {count}
            </span>
          )}
        </div>
        {link && (
          <Link to={link} className="text-[12px] font-medium flex items-center gap-0.5" style={{ color: "#B7955B" }}>
            Vedi tutto <ChevronRight className="w-3 h-3" strokeWidth={2} />
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}

/* ─── Appointment row ─── */
function ApptRow({ r }) {
  const time = r.when ? r.when.slice(11, 16) : "";
  return (
    <Link
      to={r.clientId ? `/m/clienti/${r.clientId}` : "/m/agenda"}
      className="flex items-center gap-3 px-4 py-3 active:scale-[0.99] transition-transform"
      style={{ background: "#FDFAF5", border: "1px solid rgba(183,149,91,0.25)", borderRadius: 10 }}
    >
      <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ background: "#B7955B", borderRadius: 8 }}>
        <CalendarClock className="w-5 h-5 text-white" strokeWidth={1.6} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-slate-800 truncate">{r.title}</p>
        <p className="text-[11px] tabular-nums mt-0.5" style={{ color: "#B7955B" }}>{time}</p>
      </div>
      <Clock className="w-4 h-4 shrink-0" style={{ color: "#B7955B" }} strokeWidth={1.6} />
    </Link>
  );
}

/* ─── Client call row ─── */
function ClientCallRow({ c, onMsg }) {
  const initials = `${(c.cognome || c.nome || "?").slice(0, 1)}`.toUpperCase();
  return (
    <SwipeActions
      disabled={!c.telefono}
      radius={12}
      onSwipeRight={() => { window.location.href = telLink(c.telefono); }}
      onSwipeLeft={() => onMsg?.(c)}
      testid={`mtoday-swipe-${c.id}`}
    >
    <div
      className="overflow-hidden active:scale-[0.99] transition-transform"
      style={{ background: "#fff", border: "1px solid #E8ECF2", borderRadius: 12, boxShadow: "0 1px 3px rgba(10,25,47,0.04)" }}
      data-testid={`mtoday-client-${c.id}`}
    >
      <Link to={`/m/clienti/${c.id}`} className="flex items-center gap-3 px-4 py-3">
        {/* Avatar */}
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-semibold shrink-0"
          style={{ background: "#0A192F", color: "#B7955B" }}
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-slate-800 truncate">{c.cognome} {c.nome}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            {c.stato && (
              <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 tracking-[0.06em]"
                style={{ background: "#F4F6FA", color: "#64748B", borderRadius: 3 }}>
                {c.stato}
              </span>
            )}
            {c.citta && <span className="text-[11px] text-slate-400 truncate">{c.citta}</span>}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" strokeWidth={1.5} />
      </Link>

      {/* Action row */}
      <div className="flex" style={{ borderTop: "1px solid #F0F2F5" }}>
        {c.telefono ? (
          <>
            <a
              href={telLink(c.telefono)}
              onClick={() => buzz()}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[13px] font-semibold active:bg-emerald-50 transition-colors"
              style={{ color: "#16A34A", borderRight: "1px solid #F0F2F5" }}
              data-testid={`mcall-${c.id}`}
            >
              <Phone className="w-3.5 h-3.5" strokeWidth={2} /> Chiama
            </a>
            <a
              href={waLink(c.telefono)}
              target="_blank"
              rel="noreferrer"
              onClick={() => buzz()}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[13px] font-semibold active:bg-[#25D366]/10 transition-colors"
              style={{ color: "#25D366", borderRight: "1px solid #F0F2F5" }}
              data-testid={`mwa-${c.id}`}
            >
              <MessageCircle className="w-3.5 h-3.5" strokeWidth={2} /> WA
            </a>
            <button
              type="button"
              onClick={() => { buzz(); onMsg?.(c); }}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[13px] font-semibold active:bg-sky-50 transition-colors"
              style={{ color: "#0284C7" }}
              data-testid={`mmsg-${c.id}`}
            >
              <MessageSquareText className="w-3.5 h-3.5" strokeWidth={2} /> Msg
            </button>
          </>
        ) : (
          <div className="flex-1 py-2.5 text-center text-[12px] text-slate-300">Nessun numero</div>
        )}
      </div>
    </div>
    </SwipeActions>
  );
}
